import React, { useState } from 'react';
import { Avatar, StatusPill, Progress, IconBtn, AlertChip, Menu } from '../ui';
import { formatMoneyCompact, formatPercent } from '../../lib/format';
import { getInitials, statusToPill, alertText } from './projectUtils';

/**
 * List-view table of projects. Same data ProjectCard renders, denser
 * layout. Each row is whole-row clickable → onSelect(project); the
 * kebab uses stopPropagation so menu interactions don't navigate.
 *
 * Layout (per handoff README §2e):
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │ PROJECT       CLIENT    PM       CONTRACT   BILLED  PROGRESS  …  │
 *   ├──────────────────────────────────────────────────────────────────┤
 *   │ [Av] Chico…   Chico ISD [pm] JB  $5.5M      $5.3M   ████████ 97% │
 *   │      #CHICO…                              +$221k ret             │
 *   │                                                       [alerts] ⋯ │
 *   └──────────────────────────────────────────────────────────────────┘
 *   │ 7 projects · $11.69M contract · $5.38M billed   Updated 2 min ago │
 *   └──────────────────────────────────────────────────────────────────┘
 *
 * Column widths via CSS grid template — fluid where it should be, fixed
 * where the content is constant-width (progress bar slot, kebab).
 */
const COLUMNS =
  'minmax(220px, 28%) minmax(120px, 1fr) minmax(140px, 1fr) 110px 130px 160px 200px 40px';

export default function ProjectTable({
  projects,
  projectAlerts = {},
  depAlerts = {},
  onSelect,
  onDelete,
  deletingId,
  totalContractValue,
  totalBilled,
}) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: 10,
        boxShadow: 'var(--shadow-sm)',
        overflow: 'hidden',
        marginTop: 16,
      }}
    >
      {/* Header + rows share one horizontal-scroll container so on narrow /
          zoomed viewports the fixed-width columns (incl. the far-right kebab)
          scroll into view instead of clipping off the overflow:hidden panel. */}
      <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
      {/* Header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: COLUMNS,
          gap: 12,
          padding: '10px 16px',
          background: 'var(--bg-surface-2)',
          borderBottom: '1px solid var(--border-subtle)',
          fontSize: 12.5,
          fontWeight: 500,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          color: 'var(--text-3)',
        }}
      >
        <span>Project</span>
        <span>Client</span>
        <span>PM</span>
        <span style={{ textAlign: 'right' }}>Contract</span>
        <span style={{ textAlign: 'right' }}>Billed</span>
        <span>Progress</span>
        <span>Alerts</span>
        <span />
      </div>

      {/* Rows */}
      {projects.map((project) => (
        <ProjectRow
          key={project.id}
          project={project}
          alert={projectAlerts[String(project.id)]}
          depAlert={depAlerts[String(project.id)]}
          onSelect={onSelect}
          onDelete={onDelete}
          isDeleting={deletingId === project.id}
        />
      ))}
      </div>

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 16px',
          borderTop: '1px solid var(--border-subtle)',
          fontSize: 13.5,
          color: 'var(--text-3)',
        }}
      >
        <span>
          {projects.length} project{projects.length === 1 ? '' : 's'} ·{' '}
          <span className="mono" style={{ color: 'var(--text-1)' }}>
            {formatMoneyCompact(totalContractValue || 0)}
          </span>{' '}
          contract ·{' '}
          <span className="mono" style={{ color: 'var(--text-1)' }}>
            {formatMoneyCompact(totalBilled || 0)}
          </span>{' '}
          billed
        </span>
      </div>
    </div>
  );
}

/* -------------------- row -------------------- */

function ProjectRow({ project, alert, depAlert, onSelect, onDelete, isDeleting }) {
  const [hover, setHover] = useState(false);
  const contractVal =
    parseFloat(project.revised_contract_value) ||
    parseFloat(project.contract_amount) ||
    0;
  const billedVal = parseFloat(project.total_billed) || 0;
  const retainage = parseFloat(project.total_retainage) || 0;
  const billedPct = contractVal > 0 ? (billedVal / contractVal) * 100 : 0;
  const progressColor = billedPct >= 95 ? 'var(--success)' : 'var(--accent)';
  const pill = statusToPill(project.status);
  const initials = getInitials(project);

  return (
    <div
      onClick={() => onSelect && onSelect(project)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: COLUMNS,
        gap: 12,
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: '1px solid var(--border-subtle)',
        background: hover ? 'var(--bg-hover)' : 'transparent',
        cursor: 'pointer',
        transition: 'background .1s',
        opacity: isDeleting ? 0.55 : 1,
      }}
    >
      {/* Project: avatar + name (+ inline status pill) + mono code */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        {project.logo_url ? (
          <img
            src={project.logo_url}
            alt=""
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              objectFit: 'cover',
              border: '1px solid var(--border-subtle)',
              flexShrink: 0,
            }}
          />
        ) : (
          <Avatar initials={initials} size={30} accent />
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              minWidth: 0,
            }}
          >
            <span
              style={{
                fontSize: 14.5,
                fontWeight: 600,
                color: 'var(--text-1)',
                letterSpacing: '-0.01em',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
              title={project.project_name}
            >
              {project.project_name}
            </span>
            <StatusPill status={pill.status} label={pill.label} />
          </div>
          {project.project_number && (
            <div
              className="mono"
              style={{
                fontSize: 12,
                color: 'var(--text-3)',
                marginTop: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              #{project.project_number}
            </div>
          )}
        </div>
      </div>

      {/* Client */}
      <span
        style={{
          fontSize: 14,
          color: 'var(--text-1)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={project.client_name || ''}
      >
        {project.client_name || '—'}
      </span>

      {/* PM (with small monogram avatar) */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          minWidth: 0,
        }}
      >
        {project.project_manager && project.project_manager !== '—' && (
          <Avatar
            initials={pmInitials(project.project_manager)}
            size={22}
          />
        )}
        <span
          style={{
            fontSize: 14,
            color: 'var(--text-1)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
          title={project.project_manager || ''}
        >
          {project.project_manager || '—'}
        </span>
      </div>

      {/* Contract (right) */}
      <span
        className="mono"
        style={{
          fontSize: 14,
          color: 'var(--text-1)',
          fontWeight: 500,
          textAlign: 'right',
          letterSpacing: '-0.02em',
        }}
      >
        {formatMoneyCompact(contractVal)}
      </span>

      {/* Billed (right) */}
      <div style={{ textAlign: 'right' }}>
        <span
          className="mono"
          style={{
            fontSize: 14,
            color: 'var(--text-1)',
            fontWeight: 500,
            letterSpacing: '-0.02em',
          }}
        >
          {formatMoneyCompact(billedVal)}
        </span>
        {retainage > 0 && (
          <div
            className="mono"
            style={{
              fontSize: 12,
              color: 'var(--warning)',
              marginTop: 1,
            }}
          >
            +{formatMoneyCompact(retainage)} ret
          </div>
        )}
      </div>

      {/* Progress: bar + mono percent */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <Progress value={billedPct} height={5} color={progressColor} />
        </div>
        <span
          className="mono"
          style={{
            fontSize: 12.5,
            color: 'var(--text-2)',
            minWidth: 34,
            textAlign: 'right',
          }}
        >
          {formatPercent(billedPct)}
        </span>
      </div>

      {/* Alerts (chips stacked) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {alert && (
          <AlertChip
            kind={alert.critical_count > 0 ? 'critical' : 'warning'}
            text={alertText(
              alert.alert_count,
              alert.critical_count,
              'task',
              'behind'
            )}
          />
        )}
        {depAlert && (
          <AlertChip
            kind={depAlert.critical_count > 0 ? 'critical' : 'warning'}
            text={alertText(
              depAlert.alert_count,
              depAlert.critical_count,
              'sched issue',
              ''
            )}
          />
        )}
        {!alert && !depAlert && (
          <span style={{ fontSize: 12.5, color: 'var(--text-4)' }}>—</span>
        )}
      </div>

      {/* Kebab */}
      <div style={{ textAlign: 'right' }}>
        <Menu
          trigger={(toggle) => (
            <IconBtn
              name="kebab"
              label="More actions"
              onClick={(e) => {
                e.stopPropagation();
                toggle(e);
              }}
            />
          )}
          items={[
            {
              label: 'View details',
              onClick: () => onSelect && onSelect(project),
            },
            { divider: true },
            {
              label: 'Delete project',
              danger: true,
              onClick: () => onDelete && onDelete(project),
            },
          ]}
        />
      </div>
    </div>
  );
}

/** Two-letter initials from a PM name. Handles single names, full names,
 *  or comma-separated multiple PMs (uses the first name's initials). */
function pmInitials(name) {
  if (!name) return '—';
  const first = name.split(',')[0].trim();
  const parts = first.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
